/**
 * SocialListeningPanel — "เสียงเมืองสีคิ้ว · SIKHIO MENTIONS".
 *
 * One listening stream for everything mentioning Sikhio: the news aggregator
 * (Bing News search for สีคิ้ว / นครราชสีมา / ลำตะคอง / น้ำท่วม สีคิ้ว /
 * เขาใหญ่ โคราช + Bangkok Post / Thai PBS / Matichon / Khaosod, every item
 * filtered against a relevance floor — it must name a place here — and
 * geo-tagged) merged with the municipal Facebook page, ordered by recency.
 * This is the raw "what is being said" view — distinct from NewsDesk, which
 * triages the same items into the Mayor's action queue. Keeping them separate
 * means one panel answers "what's the town talking about?" and the other
 * "what do I need to do about it?".
 *
 * Axiom Core: source chips are hairline-bordered mono, no cards, no radius;
 * the 24h volume is a mono readout with a 2px accent left-edge.
 */

import type { IntelligenceItem, FacebookPost } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";
import { safeUrl } from "../lib/safeUrl";
import { ago } from "../lib/time";

interface Mention {
  id: string;
  source: string;
  title: string;
  url: string | null;
  at: string;
  isSocial: boolean;
  placeName: string | null;
}

interface Props {
  news: IntelligenceItem[];
  facebook: FacebookPost[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: import("@sikhio/shared").FallbackTier;
  /** Tier of the Facebook leg. The source chip names only the feeds that
   *  are actually feeding this panel — the Facebook integration ships
   *  without its page credentials, and a chip reading "fb" above a panel
   *  with no Facebook in it is a claim the panel cannot back. */
  facebookTier?: import("@sikhio/shared").FallbackTier;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_ROWS = 14;

function toMentions(news: IntelligenceItem[], facebook: FacebookPost[]): Mention[] {
  const fromNews: Mention[] = news.map((n) => ({
    id: `news-${n.id}`,
    source: n.source,
    title: n.title,
    url: safeUrl(n.sourceUrl),
    at: n.publishedAt,
    isSocial: false,
    placeName: n.placeName,
  }));
  const fromFb: Mention[] = facebook.map((p) => ({
    id: `fb-${p.id}`,
    source: "Facebook",
    title: (p.message || "").replace(/\s+/g, " ").slice(0, 140) || "(รูปภาพ/โพสต์)",
    url: safeUrl(p.permalink),
    at: p.createdAt,
    isSocial: true,
    placeName: null,
  }));
  return [...fromNews, ...fromFb]
    .filter((m) => !Number.isNaN(new Date(m.at).getTime()))
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function SocialListeningPanel({ news, facebook, loading, ageMinutes, fallbackTier, facebookTier }: Props) {
  const mentions = toMentions(news, facebook);

  if (loading && mentions.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">เสียงเมืองสีคิ้ว · SIKHIO MENTIONS</div>
        <div className="skeleton" style={{ height: 10, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 10, width: "80%", marginTop: 6 }} />
        <div className="skeleton" style={{ height: 10, width: "60%", marginTop: 6 }} />
      </div>
    );
  }

  const now = Date.now();
  const last24h = mentions.filter((m) => now - new Date(m.at).getTime() <= DAY_MS);
  // Busiest source in the last 24h — the "who's driving the conversation" cue.
  const bySource = new Map<string, number>();
  for (const m of last24h) bySource.set(m.source, (bySource.get(m.source) ?? 0) + 1);
  const topSource = [...bySource.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;
  const visible = mentions.slice(0, MAX_ROWS);

  return (
    <div className="col" aria-busy={loading || undefined}>
      <PanelHeader
        title="เสียงเมืองสีคิ้ว · SIKHIO MENTIONS"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source={facebookTier === "live" ? "bing-news·fb·rss" : "bing-news·rss"}
      />

      {/* 24h volume — a single readout with an accent left-edge, not a card. */}
      <div className="mentions-volume">
        <span className="mentions-volume-num mono">{last24h.length}</span>
        <span className="mentions-volume-unit caption">การกล่าวถึง · 24 ชม.</span>
        {topSource && (
          <span className="mentions-volume-top caption mono" title="Busiest source in the last 24h">
            {topSource[0]} ×{topSource[1]}
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="caption" style={{ color: "var(--text-3)", marginTop: 8 }}>
          ยังไม่พบการกล่าวถึงสีคิ้วในช่วงนี้
        </div>
      ) : (
        <ul className="mentions-list" role="list">
          {visible.map((m) => {
            const row = (
              <>
                <span className="mentions-row-head">
                  <span className={`mentions-src mono ${m.isSocial ? "is-social" : ""}`}>{m.source}</span>
                  <span className="mentions-ago mono caption">{ago(m.at)}</span>
                </span>
                <span className="mentions-title">{m.title}</span>
                {m.placeName && <span className="mentions-place caption mono">◎ {m.placeName}</span>}
              </>
            );
            return (
              <li key={m.id} className="mentions-row">
                {m.url ? (
                  <a href={m.url} target="_blank" rel="noreferrer noopener" className="mentions-link">
                    {row}
                  </a>
                ) : (
                  <div className="mentions-link mentions-link--dead">{row}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
